// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { IconWrapper } from '@insite/mobius/Icon';
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { LazyPicture } from "@plumberssupply/MobiusOverrides/LazyImage";
import IconArrowRight from "@plumberssupply/XDL/Icons/IconArrowRight";
import { color, ellipsis, iconButtonCommonStyles, xScreen } from "@plumberssupply/XDL/Utilities";
import { mergeWithCss } from "@plumberssupply/XDL/Utilities/mergeWithCss";
import * as React from "react";
import { css } from "styled-components";

interface OwnProps {
    imageSrc: string;
    mediumImageSrc: string;
    smallImageSrc: string;
    altText: string;
    title: string;
    description: string;
    link?: string;
    buttonLabel: string;
    openInNewTab?: boolean;
    extendedStyles?: InfoCardStyles;
}

type Props = OwnProps;

export interface InfoCardStyles {
    container?: InjectableCss;
    headingText?: TypographyPresentationProps;
    description?: TypographyProps;
    image?: LazyImageProps;
    textWrapper?: InjectableCss;
    cardButton?: ButtonPresentationProps;
}

export const infoCardStyles: InfoCardStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            height: 100%;

            ${xScreen("lte", 1200)} {
                margin-bottom: 32px;
            }

            ${xScreen(768, 1200)} {
                flex-direction: row;
            }
        `,
    },
    image: {
        width: "100%",
        height: "160px",
        css: css`
            img {
                width: 100%;
                object-fit: contain;
            }
            ${xScreen(768, 1200)} {
                max-width: 378px;
                margin-right: 16px;
                flex-shrink: 0;
            }

            ${xScreen("lte", "sm")} {
                max-width: 352px;
                height: 152px;
            }
        `,
    },
    headingText: {
        forwardAs: "h2",
        size: 20,
        weight: 800,
        color: "common.ui11",
        css: css`
            line-height: 24px;
            margin-bottom: 12px;
            ${ellipsis(1)}
        `,
    },
    description: {
        as: "p",
        size: 14,
        lineHeight: "22px",
        color: "text.main",
        weight: 400,
        css: css`
            ${ellipsis(3)}
            margin-bottom: 17px;
        `,
    },
    textWrapper: {
        css: css`
            flex-grow: 1;
            margin-top: 20px;
            ${xScreen(768, 1200)} {
                margin-top: 0;
                margin-right: 20px;
            }
        `,
    },
    cardButton: {
        ...mergeWithCss(iconButtonCommonStyles(IconArrowRight, 18, "right"), {
            css: css`
                padding: 0;
                min-height: unset;
                
                ${TypographyStyle}{
                    font-size: 16px;
                    line-height: 26px;
                }
                > ${IconWrapper} {
                    color: ${color("text.link")};
                    vertical-align: text-bottom;
                    opacity: 1;
                    top: 0;
                }

                &:hover {
                    ${IconWrapper} {
                        color: ${color("common.ui1")};
                    }
                }
            `
        })

    },
};

const InfoCard: React.FC<Props> = ({ link, openInNewTab, imageSrc, mediumImageSrc, smallImageSrc, title, altText, description, buttonLabel, extendedStyles }) => {
    const [styles] = React.useState(() => mergeWithCss(infoCardStyles, extendedStyles));

    return (
        <StyledWrapper {...styles.container} data-test-selector={`InfoCard-${title}`}>
            <LazyPicture largeImageSrc={imageSrc} mediumImageSrc={mediumImageSrc} smallImageSrc={smallImageSrc} altText={altText} {...styles.image} />
            <StyledWrapper {...styles.textWrapper}>
                <Typography className="card-heading" {...styles.headingText}>
                    {title}
                </Typography>
                <Typography {...styles.description}>{description}</Typography>

                {(buttonLabel || link) &&
                    <Clickable href={link} target={openInNewTab ? "_blank" : undefined}>
                        <Button className="card-button" {...styles.cardButton}>
                            {buttonLabel || link}
                        </Button>
                    </Clickable>
                }
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default InfoCard;
